import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { getInvoices, createInvoice, getPayments, createPayment, getDashboardFinance, getInvoicePDF } from '../lib/api';
import { Plus, Search, DollarSign, Receipt, CreditCard, TrendingUp, AlertCircle, CheckCircle, Download, FileText } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';

const CHART_COLORS = ['#10B981', '#D97706', '#E11D48', '#6366F1'];
const PAYMENT_METHODS = ['eft', 'card', 'cash', 'payfast', 'other'];

const statusColors = {
  draft: 'bg-slate-100 text-slate-700',
  pending: 'bg-amber-100 text-amber-700',
  partial: 'bg-blue-100 text-blue-700',
  paid: 'bg-emerald-100 text-emerald-700',
  overdue: 'bg-rose-100 text-rose-700'
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0 }).format(amount);
};

// Invoice Form Component
const InvoiceForm = ({ onSave, onClose }) => {
  const [form, setForm] = useState({
    client_name: '', client_email: '', items: [{ description: '', quantity: 1, unit_price: 0 }],
    subtotal: 0, vat_rate: 15, vat_amount: 0, total: 0, currency: 'ZAR', status: 'pending',
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], notes: ''
  });
  const [saving, setSaving] = useState(false);

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const vatAmount = subtotal * (form.vat_rate / 100);
    return { subtotal, vat_amount: vatAmount, total: subtotal + vatAmount };
  };

  const updateItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    const totals = calculateTotals(newItems);
    setForm({ ...form, items: newItems, ...totals });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit_price: 0 }] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createInvoice(form);
      toast.success('Invoice created');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Client Name *</Label>
          <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required data-testid="invoice-client-input" />
        </div>
        <div className="space-y-2">
          <Label>Client Email</Label>
          <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Line Items</Label>
        <div className="border rounded-lg divide-y">
          {form.items.map((item, idx) => (
            <div key={idx} className="p-3 grid grid-cols-4 gap-2 items-end">
              <div className="col-span-2">
                <Input placeholder="Description" value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} />
              </div>
              <Input type="number" placeholder="Qty" min="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)} />
              <Input type="number" placeholder="Unit Price" min="0" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
            </div>
          ))}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={addItem}>+ Add Item</Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Due Date</Label>
          <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>VAT Rate (%)</Label>
          <Input type="number" value={form.vat_rate} onChange={(e) => {
            const rate = parseFloat(e.target.value) || 0;
            const vatAmount = form.subtotal * (rate / 100);
            setForm({ ...form, vat_rate: rate, vat_amount: vatAmount, total: form.subtotal + vatAmount });
          }} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 text-right pt-4 border-t">
        <div></div>
        <div className="text-sm text-muted-foreground">Subtotal:</div>
        <div className="font-mono font-semibold">{formatCurrency(form.subtotal)}</div>
        <div></div>
        <div className="text-sm text-muted-foreground">VAT ({form.vat_rate}%):</div>
        <div className="font-mono">{formatCurrency(form.vat_amount)}</div>
        <div></div>
        <div className="font-semibold">Total:</div>
        <div className="font-mono font-bold text-lg">{formatCurrency(form.total)}</div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-invoice-btn">{saving ? 'Saving...' : 'Create Invoice'}</Button>
      </div>
    </form>
  );
};

// Payment Form Component
const PaymentForm = ({ invoices, onSave, onClose }) => {
  const [form, setForm] = useState({
    invoice_id: '', amount: 0, method: 'eft', reference: '', notes: ''
  });
  const [saving, setSaving] = useState(false);
  const selectedInvoice = invoices.find(i => i.id === form.invoice_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createPayment(form);
      toast.success('Payment recorded');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Invoice *</Label>
        <Select value={form.invoice_id} onValueChange={(v) => setForm({ ...form, invoice_id: v })}>
          <SelectTrigger data-testid="payment-invoice-select"><SelectValue placeholder="Select invoice" /></SelectTrigger>
          <SelectContent>
            {invoices.filter(i => i.balance > 0).map(inv => (
              <SelectItem key={inv.id} value={inv.id}>
                {inv.invoice_number} - {inv.client_name} (Balance: {formatCurrency(inv.balance)})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {selectedInvoice && (
        <div className="p-4 bg-muted rounded-lg">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Total</p>
              <p className="font-mono font-semibold">{formatCurrency(selectedInvoice.total)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Paid</p>
              <p className="font-mono font-semibold text-emerald-600">{formatCurrency(selectedInvoice.amount_paid)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Balance</p>
              <p className="font-mono font-semibold text-amber-600">{formatCurrency(selectedInvoice.balance)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Amount *</Label>
          <Input type="number" min="0" max={selectedInvoice?.balance || 0} value={form.amount} 
            onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} 
            required data-testid="payment-amount-input" />
        </div>
        <div className="space-y-2">
          <Label>Payment Method</Label>
          <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAYMENT_METHODS.map(m => (
                <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Reference</Label>
        <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Transaction reference or receipt number" />
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving || !form.invoice_id || form.amount <= 0} data-testid="save-payment-btn">
          {saving ? 'Processing...' : 'Record Payment'}
        </Button>
      </div>
    </form>
  );
};

// Main Finance Page Component
export const FinancePage = () => {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load data independently so one failure doesn't block others
      const [invRes, payRes, dashRes] = await Promise.allSettled([
        getInvoices(), getPayments(), getDashboardFinance()
      ]);
      
      if (invRes.status === 'fulfilled') {
        setInvoices(invRes.value.data);
      } else {
        console.error('Failed to load invoices:', invRes.reason);
      }
      
      if (payRes.status === 'fulfilled') {
        setPayments(payRes.value.data);
      } else {
        console.error('Failed to load payments:', payRes.reason);
      }
      
      if (dashRes.status === 'fulfilled') {
        setDashboard(dashRes.value.data);
      } else {
        console.error('Failed to load dashboard:', dashRes.reason);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    inv.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  const agingData = dashboard?.aging_analysis ?
    Object.entries(dashboard.aging_analysis).map(([name, value]) => ({
      name: name + ' days',
      value
    })) : [];

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="finance-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Financial Management</h1>
          <p className="text-muted-foreground mt-1">Invoices, payments, and financial overview</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-slate-100 flex items-center justify-center">
                <Receipt className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Invoiced</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(dashboard?.total_invoiced || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Collected</p>
                <p className="text-2xl font-bold font-mono text-emerald-600">{formatCurrency(dashboard?.total_collected || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Outstanding</p>
                <p className="text-2xl font-bold font-mono text-amber-600">{formatCurrency(dashboard?.total_outstanding || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Collection Rate</p>
                <p className="text-2xl font-bold font-mono">{dashboard?.collection_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Receivables Aging Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {agingData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full justify-start" variant="outline" data-testid="create-invoice-btn">
                  <Receipt className="h-4 w-4 mr-2" /> Create Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Create Invoice</DialogTitle>
                </DialogHeader>
                <InvoiceForm onSave={loadData} onClose={() => setInvoiceDialogOpen(false)} />
              </DialogContent>
            </Dialog>

            <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
              <DialogTrigger asChild>
                <Button className="w-full justify-start" variant="outline" data-testid="record-payment-btn">
                  <CreditCard className="h-4 w-4 mr-2" /> Record Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>Record Payment</DialogTitle>
                </DialogHeader>
                <PaymentForm invoices={invoices} onSave={loadData} onClose={() => setPaymentDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <Tabs defaultValue="invoices" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="invoices-tab">Invoices ({invoices.length})</TabsTrigger>
            <TabsTrigger value="payments" data-testid="payments-tab">Payments ({payments.length})</TabsTrigger>
          </TabsList>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search..." className="pl-9 w-64" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        <TabsContent value="invoices">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="font-mono text-sm">{inv.invoice_number}</TableCell>
                    <TableCell className="font-medium">{inv.client_name}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(inv.total)}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-600">{formatCurrency(inv.amount_paid)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-600">{formatCurrency(inv.balance)}</TableCell>
                    <TableCell className="text-sm">{inv.due_date?.split('T')[0] || '-'}</TableCell>
                    <TableCell><Badge className={statusColors[inv.status]}>{inv.status}</Badge></TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const response = await getInvoicePDF(inv.id);
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `invoice_${inv.invoice_number}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                            toast.success('Invoice PDF downloaded');
                          } catch (error) {
                            toast.error('Failed to download PDF');
                          }
                        }}
                        title="Download PDF"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredInvoices.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No invoices found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => {
                  const invoice = invoices.find(i => i.id === payment.invoice_id);
                  return (
                    <TableRow key={payment.id}>
                      <TableCell className="text-sm">{payment.created_at?.split('T')[0]}</TableCell>
                      <TableCell className="font-mono text-sm">{invoice?.invoice_number || '-'}</TableCell>
                      <TableCell className="uppercase text-sm">{payment.method}</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-600">{formatCurrency(payment.amount)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{payment.reference || '-'}</TableCell>
                    </TableRow>
                  );
                })}
                {payments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No payments recorded</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
